import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Linkedin, Youtube } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LOGO_URL } from "../assets/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Redes sociais do CIEES Portugal.
 * Para ativar uma rede, basta substituir o "#" pelo URL real.
 * Redes com "#" ainda assim aparecem mas com aviso (placeholder).
 * Em produção (URL preenchido), os links abrem em nova aba.
 */
const SOCIAL_LINKS = [
  { key: "instagram", label: "Instagram", url: "#", Icon: Instagram },
  { key: "linkedin", label: "LinkedIn", url: "#", Icon: Linkedin },
  { key: "youtube", label: "YouTube", url: "#", Icon: Youtube },
  { key: "whatsapp", label: "WhatsApp", url: "#", Icon: WhatsAppIcon },
];

function WhatsAppIcon({ className = "" }) {
  // Lucide ainda não tem WhatsApp nativo — SVG oficial inline (24×24).
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.768.967-.941 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.273.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
    </svg>
  );
}

export default function Footer() {
  const { t, lang } = useLang();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, name: name || null, language: lang });
      toast.success(t.newsletter.success);
      setEmail("");
      setName("");
    } catch (err) {
      toast.error("Erro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[hsl(var(--primary))] text-white mt-24" data-testid="site-footer">
      <div className="container-page py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CIEES" className="h-14 w-14" />
            <div>
              <div className="display-heading text-2xl">CIEES Portugal</div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/70">
                {t.footer.tagline}
              </div>
            </div>
          </div>
          <p className="text-white/75 text-sm mt-6 max-w-md leading-relaxed">
            {t.home.sectionAboutBody}
          </p>

          {/* Social icons — atualize os URLs em SOCIAL_LINKS no topo do ficheiro */}
          <ul className="mt-7 flex items-center gap-4" data-testid="footer-social">
            {SOCIAL_LINKS.map((s) => {
              const isPlaceholder = s.url === "#";
              return (
                <li key={s.key}>
                  <a
                    href={s.url}
                    {...(!isPlaceholder && { target: "_blank", rel: "noopener noreferrer" })}
                    aria-label={s.label}
                    title={isPlaceholder ? `${s.label} (em breve)` : s.label}
                    className={`inline-flex items-center justify-center h-9 w-9 border border-white/25 transition-colors ${
                      isPlaceholder
                        ? "text-white/40 cursor-not-allowed"
                        : "text-white/80 hover:text-[hsl(var(--accent))] hover:border-[hsl(var(--accent))]"
                    }`}
                    data-testid={`footer-social-${s.key}`}
                    onClick={(e) => { if (isPlaceholder) e.preventDefault(); }}
                  >
                    <s.Icon className="h-4 w-4" />
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="overline text-white/70">{t.footer.links}</div>
          <ul className="mt-4 space-y-2 text-sm">
            <li><Link to="/sobre" className="hover:text-[hsl(var(--accent))] transition-colors">{t.nav.about}</Link></li>
            <li><Link to="/espiritualidade" className="hover:text-[hsl(var(--accent))] transition-colors">{t.nav.spirituality}</Link></li>
            <li><Link to="/eventos" className="hover:text-[hsl(var(--accent))] transition-colors">{t.nav.events}</Link></li>
            <li><Link to="/apoiar" className="hover:text-[hsl(var(--accent))] transition-colors" data-testid="footer-support-link">{t.nav.support}</Link></li>
            <li><Link to="/contactos" className="hover:text-[hsl(var(--accent))] transition-colors">{t.nav.contact}</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="overline text-white/70">{t.newsletter.title}</div>
          <p className="text-sm text-white/75 mt-3 mb-4">{t.newsletter.body}</p>
          <form onSubmit={submit} className="flex flex-col gap-2" data-testid="newsletter-form">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.newsletter.namePlaceholder || "Nome (opcional)"}
              className="bg-transparent border border-white/30 px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="newsletter-name-input"
              maxLength={120}
            />
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.newsletter.placeholder}
                className="flex-1 bg-transparent border border-white/30 px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-[hsl(var(--accent))] text-[#1A201F] text-xs uppercase tracking-[0.18em] font-semibold disabled:opacity-60 hover:bg-[#B3922E] transition-colors"
                data-testid="newsletter-submit-button"
              >
                {loading ? "…" : t.newsletter.cta}
              </button>
            </div>
          </form>

          <div className="mt-8 text-sm text-white/75">
            <div className="overline text-white/70">{t.footer.seat}</div>
            <div className="mt-2">Praça Damão, 7</div>
            <div>1400-085 Lisboa</div>
            <div>Portugal</div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/15">
        <div className="container-page py-5 flex flex-col sm:flex-row items-center justify-between text-xs text-white/60">
          <div>{t.footer.copyright}</div>
          <div className="mt-2 sm:mt-0 flex items-center gap-4">
            <Link to="/membros/login" className="hover:text-white transition-colors" data-testid="footer-member-link">
              Área de sócios
            </Link>
            <span className="opacity-30">·</span>
            <Link to="/admin/login" className="hover:text-white transition-colors" data-testid="footer-admin-link">
              Administração
            </Link>
            <span className="opacity-30">·</span>
            <span>{t.footer.tagline}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
